import { useTranslation } from 'react-i18next';

import { SmDashboardPage } from './DashboardPage.sm';

import { TextButton } from '@speeki/global-ui-components';

import DashboardNicole from '@assets/images/nicole_dashboard.png';

import { Container } from '@components/ions/layouts';
import { ShowMoreWrapper } from '@components/molecules/ShowMoreWrapper/ShowMoreWrapper';
import { DefaultLayout } from '@components/templates/DefaultLayout/DefaultLayout';

import { normalizeRichTextWhitespace } from '@utils/helpers';
import { useBackToEntitySelection } from '@utils/hooks/useBackToEntitySelection';
import { useResponsive } from '@utils/hooks/useResponsive';
import { useScrollToElement } from '@utils/hooks/useScrollToElement';
import { useServiceRedirect } from '@utils/hooks/useServiceRedirect';
import { useServicesStatistics } from '@utils/hooks/useServicesStatistics';
import { useTenantByUuid } from '@utils/hooks/useTenantByUuid';

import { AuthService } from '@services/AuthService/AuthService';

import { useReactiveVar } from '@apollo/client';
import { authVar } from '@boot/reactiveVariables';
import { SkeletonDashboardPage } from '@domains/dashboard/DashboardPage.skeleton';
import { CompanyPolicies } from '@domains/dashboard/organisms/CompanyPolicies/CompanyPolicies';
import { MicroservicesTiles } from '@domains/dashboard/organisms/MicroservicesTiles/MicroservicesTiles';

import {
    CompanyText,
    ContentWrapper,
    DashboardPageWrapper,
    DashboardTitle,
    MainContentWrapper,
    NicoleImage,
    PlatformDescription,
    ScrollLink,
} from './DashboardPage.styled';
import { CompanyPoliciesContent } from './organisms/CompanyPolicies/CompanyPolicies.styled';

export const DashboardPage = () => {
    useServiceRedirect({ path: '/' });
    useServicesStatistics();
    const tenantUuid = AuthService.getTenantUuid();
    const { t } = useTranslation();
    const isLg = useResponsive('lg');
    const isSm = useResponsive('sm');
    const isLoggedIn = useReactiveVar(authVar).isLoggedIn;

    const { ref: policySectionRef, scrollToElement } =
        useScrollToElement<HTMLDivElement>({});

    const { onBackToEntity } = useBackToEntitySelection();

    const { data, loading } = useTenantByUuid({
        skip: !tenantUuid,
        variables: {
            uuid: tenantUuid ?? '',
        },
    });
    const headline = data?.tenant?.headline;
    const homeContent = data?.tenant?.homeContent;
    const isNicole = data?.tenant.assetSettings.nicoleSetting === 'DEFAULT';
    const modules = data?.tenant.modules;
    const policiesContent = data?.tenant?.policiesContent;
    const tenantName = data?.tenant?.name;
    const regex = /(<([^>]+)>)/gi; // remove HTML tags
    const clearHeadline = headline
        ?.replace(regex, '')
        .replace(/&nbsp;/g, ' ')
        .trim();

    const [companySlug] = window.location.host.split('.');

    if (isSm)
        return <SmDashboardPage modules={modules} tenantName={tenantName} />;

    if (loading) return <SkeletonDashboardPage />;

    return (
        <DefaultLayout
            contentPadding={{
                bottom: 0,
                top: 0,
            }}
            headerProps={{ withLoginActions: true }}
            hideBackgroundImage
        >
            <DashboardPageWrapper>
                <ContentWrapper>
                    <Container>
                        <DashboardTitle>
                            {clearHeadline?.length ? (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: normalizeRichTextWhitespace(
                                            headline,
                                        ),
                                    }}
                                />
                            ) : (
                                <>
                                    {t('WELCOME_TO')} {tenantName}{' '}
                                    {t('SOLUTION_TO_MANAGE')}
                                </>
                            )}
                        </DashboardTitle>
                        <MainContentWrapper>
                            <PlatformDescription>
                                {!isLoggedIn && (
                                    <TextButton
                                        iconProps={{
                                            icon: 'SvgChevronLeft24',
                                            size: 24,
                                            themeColors: {
                                                default: {
                                                    'color-1':
                                                        'tertiary.default',
                                                },
                                                hover: {
                                                    'color-1': 'tertiary.hover',
                                                },
                                            },
                                        }}
                                        onClick={() =>
                                            onBackToEntity(companySlug)
                                        }
                                        testId="y2CGB2E"
                                    >
                                        {t('BACK_TO_ENTITY_SELECTION')}
                                    </TextButton>
                                )}
                                <ShowMoreWrapper testId="NeoqjMQ">
                                    <CompanyPoliciesContent
                                        className={'ql-editor'}
                                        dangerouslySetInnerHTML={{
                                            __html: normalizeRichTextWhitespace(
                                                homeContent,
                                            ),
                                        }}
                                    />
                                </ShowMoreWrapper>
                                <CompanyText>
                                    {t('DASHBOARD_SUBTITLE')}{' '}
                                    <ScrollLink onClick={scrollToElement}>
                                        {t('MY_POLICIES_TEXT')}
                                    </ScrollLink>{' '}
                                    {t('BELOW')}.
                                </CompanyText>
                                {!isLg && isNicole && (
                                    <NicoleImage>
                                        <img
                                            alt="nicole"
                                            src={DashboardNicole as string}
                                        />
                                    </NicoleImage>
                                )}
                            </PlatformDescription>
                            {modules ? (
                                <MicroservicesTiles modules={modules} />
                            ) : null}
                        </MainContentWrapper>
                    </Container>
                </ContentWrapper>
                <Container ref={policySectionRef}>
                    {policiesContent && (
                        <CompanyPolicies policiesContent={policiesContent} />
                    )}
                </Container>
            </DashboardPageWrapper>
        </DefaultLayout>
    );
};
